import { AVAILABLE_ACTIONS_QUERY_KEYS, QueryKeys } from "@/constants/Query";
import { useActionHistory } from "@/hooks/useActionHistory";
import { type ApiError, useApi } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { wildcardInvalidateQuery } from "@/lib/utils";
import { UserAction } from "@/types/actionHistory";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

export interface CancelEncounterParams {
  cancelReasonCode: string;
  cancelReasonDisplay: string;
}

export interface EncounterResponse {
  encounterId: string;
}

export interface EncounterError {
  detail: string;
}

function invalidatePatientQueries(queryClient: QueryClient, patientId: string) {
  queryClient.refetchQueries({
    queryKey: QueryKeys.Provider.PATIENT_TABS,
  });
  queryClient.invalidateQueries({
    queryKey: QueryKeys.Patient.Chart.ALL({
      patientId: patientId,
    }),
  });
  for (const queryKey of AVAILABLE_ACTIONS_QUERY_KEYS) {
    wildcardInvalidateQuery(
      queryClient,
      queryKey.filter((key) => key !== undefined),
    );
  }
}

export const useCancelEncounter = ({
  encounterId,
  patientId,
  onSuccess,
  onError,
}: {
  encounterId: string;
  patientId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation<
    EncounterResponse,
    CancelEncounterParams,
    EncounterError
  >({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.CANCEL,
      mutationFn: (params: CancelEncounterParams) => {
        return axiosInstance.post(
          `/new-api/encounter/${encounterId}/cancel`,
          params,
        );
      },
      onError,
      onSuccess: () => {
        invalidatePatientQueries(queryClient, patientId);
        onSuccess?.();
      },
    },
  });
};

export interface Icd10Coding {
  code: string;
  display: string;
  system: string;
  version: string;
}

/** Clinician's ACCESS enrollment decision, sent with the close for ACCESS visits. */
export interface ProgramEnrollment {
  enrollmentApproved: boolean;
  primaryIcd10Code: Icd10Coding;
  additionalIcd10Codes: Icd10Coding[];
}

export interface CloseEncounterParams {
  incomplete?: boolean;
  designatedForReturn?: boolean;
  programEnrollment?: ProgramEnrollment;
}

export interface CloseEncounterError {
  detail: {
    failureDebugText: string;
  };
}

export const useCloseEncounter = ({
  patientId,
  encounterId,
  onSuccess,
  onError,
  onMutate,
}: {
  patientId: string;
  encounterId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError | AxiosError<CloseEncounterError>) => void;
  onMutate?: (variables: CloseEncounterParams) => void;
}) => {
  const queryClient = useQueryClient();
  const { trackAction: trackActionHistory } = useActionHistory();
  return useApiMutation<
    EncounterResponse,
    CloseEncounterParams,
    CloseEncounterError
  >({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.CLOSE,
      mutationFn: ({
        incomplete = false,
        programEnrollment,
      }: CloseEncounterParams) => {
        return axiosInstance.post("/new-api/encounter/close", {
          encounterId,
          incomplete,
          validatePrereqs: true,
          ...(programEnrollment ? { programEnrollment } : {}),
        });
      },
      onError,
      onMutate,
      onSuccess: () => {
        trackActionHistory(UserAction.ENCOUNTER_CLOSED, {
          patientId: patientId,
          encounterId: encounterId,
        });
        invalidatePatientQueries(queryClient, patientId);
        onSuccess?.();
      },
    },
  });
};

export enum WaitingRoomType {
  CA = "ca",
  CLINICIAN = "clinician",
}

export interface HandoffEncounterParams {
  waitingRoomType: WaitingRoomType;
}

export interface HandoffEncounterError {
  detail:
    | {
        debugMessage: string;
        encounterId: string;
        errorCode: string;
      }
    | string;
}

export const useHandoffEncounter = ({
  patientId,
  encounterId,
  onSuccess,
  onError,
  onMutate,
}: {
  patientId: string;
  encounterId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError | AxiosError<HandoffEncounterError>) => void;
  onMutate?: (variables: HandoffEncounterParams) => void;
}) => {
  const queryClient = useQueryClient();
  const { trackAction: trackActionHistory } = useActionHistory();
  return useApiMutation<
    EncounterResponse,
    HandoffEncounterParams,
    HandoffEncounterError
  >({
    mutationOptions: {
      mutationKey: QueryKeys.Distribution.HANDOFF,
      mutationFn: ({ waitingRoomType }) => {
        return axiosInstance.post("/new-api/distribution/handoff-encounter", {
          encounterId,
          patientId,
          waitingRoomType,
        });
      },
      onError,
      onMutate,
      onSuccess: () => {
        trackActionHistory(UserAction.ENCOUNTER_HANDED_OFF, {
          patientId: patientId,
          encounterId: encounterId,
        });
        invalidatePatientQueries(queryClient, patientId);
        onSuccess?.();
      },
    },
  });
};

export const usePauseEncounter = ({
  encounterId,
  patientId,
  onSuccess,
  onError,
}: {
  encounterId: string;
  patientId: string;
  onSuccess?: () => void;
  onError: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  const { trackAction: trackActionHistory } = useActionHistory();
  return useApiMutation<EncounterResponse, void, EncounterError>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.PAUSE,
      mutationFn: () =>
        axiosInstance.put(`/new-api/encounter/${encounterId}/pause`),
      onError,
      onSuccess: () => {
        trackActionHistory(UserAction.ENCOUNTER_PAUSED, {
          patientId: patientId,
          encounterId: encounterId,
        });
        invalidatePatientQueries(queryClient, patientId);
        onSuccess?.();
      },
    },
  });
};

export const useStartAmazonClinicalNote = ({
  encounterId,
  patientId,
  onSuccess,
  onError,
}: {
  encounterId: string;
  patientId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation<EncounterResponse, void, EncounterError>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.CANCEL,
      mutationFn: () => {
        return axiosInstance.post("/new-api/encounter/children", {
          motherEncounterId: encounterId,
        });
      },
      onError,
      onSuccess: () => {
        invalidatePatientQueries(queryClient, patientId);
        onSuccess?.();
      },
    },
  });
};

export const useStealEncounter = ({
  encounterId,
  patientId,
  targetPractitionerId,
  onSuccess,
  onError,
}: {
  encounterId: string;
  patientId: string;
  targetPractitionerId: string | undefined;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation<EncounterResponse, void, EncounterError>({
    mutationOptions: {
      mutationKey: QueryKeys.Distribution.STEAL,
      mutationFn: () => {
        return axiosInstance.post("/new-api/distribution/steal-encounter", {
          encounterId,
          targetPractitionerId,
        });
      },
      onError,
      onSuccess: () => {
        invalidatePatientQueries(queryClient, patientId);
        onSuccess?.();
      },
    },
  });
};

interface IsEncounterClosableResponse {
  closable: boolean;
  reason?: string;
}

export const useIsEncounterClosable = ({
  patientId,
  encounterId,
  enabled = true,
}: {
  patientId: string;
  encounterId: string;
  enabled?: boolean;
}) => {
  return useApi<IsEncounterClosableResponse>({
    queryOptions: {
      queryFn: async () => {
        const response = await axiosInstance.get(
          `/encounter/${encounterId}/closable`,
        );
        return response.data;
      },
      queryKey: QueryKeys.Patient.Chart.CLOSABLE(patientId, encounterId),
      enabled: Boolean(patientId && encounterId) && enabled,
      refetchOnWindowFocus: false,
      gcTime: 0,
    },
  });
};

export interface RejectAsyncRequestParams {
  cancelReasonCode: string;
  cancelReasonText?: string;
}

export const useRejectAsyncRequest = ({
  encounterId,
  patientId,
  onSuccess,
  onError,
}: {
  encounterId: string;
  patientId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation<
    EncounterResponse,
    RejectAsyncRequestParams,
    EncounterError
  >({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.REJECT_ASYNC_REQUEST,
      mutationFn: (params: RejectAsyncRequestParams) => {
        return axiosInstance.post(
          `/new-api/encounter/${encounterId}/reject-async-request`,
          params,
        );
      },
      onError,
      onSuccess: () => {
        invalidatePatientQueries(queryClient, patientId);
        onSuccess?.();
      },
    },
  });
};
